import {Trans} from '@lingui/macro';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {useLinkNavigation} from '../../../components/common/navigation/useLinkNavigation';
import {
  SettingsDevelopersApplicationFeaturesUrl,
  SettingsDevelopersApplicationInfoUrl,
  SettingsDevelopersApplicationOAuthUrl,
  SettingsDevelopersApplicationServersUrl,
  SettingsDevelopersPagesEnum,
  SettingsDevelopersUrl,
} from '../../../models/url/settingsUrlModel';
import {DevelopersDeleteAppDialog} from '../dialog/developersDeleteAppDialog';
import {SettingsDevelopersHeaderFeaturesActionContainer} from './actions/settingsDevelopersHeaderFeaturesActionContainer';
import {SettingsDevelopersHeaderTokenActionContainer} from './actions/settingsDevelopersHeaderTokenActionContainer';

/*
 * Constants.
 */

const applicationPublishingDocsUrl = 'https://dev.frontapp.com/docs/welcome';

/*
 * Props.
 */

interface SettingsDevelopersHeaderActionProps {
  url: SettingsDevelopersUrl;
}

/*
 * Style.
 */

const StyledButtonContentWrapperDiv = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const StyledIcon = styled(Icon)`
  color: ${(p) => p.theme.greys.shade80};
`;

/*
 * Component.
 */

export const SettingsDevelopersHeaderAction: FC<SettingsDevelopersHeaderActionProps> = (props) => {
  const {url} = props;
  const {navigateToLink} = useLinkNavigation();
  const [shouldShowDeleteDialog, setShouldShowDeleteDialog] = useState(false);

  const renderDefaultActions = () => (
    <>
      {renderApiDocumentationButton(navigateToLink)}
      {isInApplicationPage(url) && renderDeleteAction(() => setShouldShowDeleteDialog(true))}
      {shouldShowDeleteDialog && isInApplicationPage(url) && (
        <DevelopersDeleteAppDialog
          applicationId={url.applicationId}
          onRequestClose={() => setShouldShowDeleteDialog(false)}
        />
      )}
    </>
  );

  switch (url.subPage) {
    case SettingsDevelopersPagesEnum.TOKENS:
      if (url.tokenId) {
        return <SettingsDevelopersHeaderTokenActionContainer tokenId={url.tokenId} />;
      }
      return renderDefaultActions();
    case SettingsDevelopersPagesEnum.APPLICATION_FEATURES:
      if (url.feature) {
        return <SettingsDevelopersHeaderFeaturesActionContainer url={url} />;
      }
      return renderDefaultActions();
    case SettingsDevelopersPagesEnum.APPLICATION_SERVERS:
      if (url.isConnecting) {
        return null;
      }
      // TODO: Add support for deleting a server.
      if (url.serverId) {
        return null;
      }
      return renderDefaultActions();
    default:
      return renderDefaultActions();
  }
};

/*
 * Helpers.
 */

function renderApiDocumentationButton(navigateToLink: HandlerOf<string>) {
  return (
    <Button
      onClick={(event) => {
        event.preventDefault();
        navigateToLink(applicationPublishingDocsUrl);
      }}
    >
      <StyledButtonContentWrapperDiv>
        <Trans id="developers-application-header-api-documentation-action">API documentation</Trans>
        <StyledIcon name="externalLinkSmall" />
      </StyledButtonContentWrapperDiv>
    </Button>
  );
}

function renderDeleteAction(onClick: Handler) {
  return (
    <Button variant={ButtonVariantsEnum.SECONDARY_DANGER} onClick={onClick}>
      <Trans id="developers-application-header-delete-app-action">Delete</Trans>
    </Button>
  );
}

function isInApplicationPage(
  url: SettingsDevelopersUrl,
): url is
  | SettingsDevelopersApplicationFeaturesUrl
  | SettingsDevelopersApplicationInfoUrl
  | SettingsDevelopersApplicationServersUrl
  | SettingsDevelopersApplicationOAuthUrl {
  return (
    url.subPage === SettingsDevelopersPagesEnum.APPLICATION_FEATURES ||
    url.subPage === SettingsDevelopersPagesEnum.APPLICATION_INFO ||
    url.subPage === SettingsDevelopersPagesEnum.APPLICATION_OAUTH ||
    url.subPage === SettingsDevelopersPagesEnum.APPLICATION_SERVERS
  );
}
